import { type FC, useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, ArrowRight, Loader2, Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Tag } from "@/shared/components/ui/tag";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import { PasswordRequirement } from "@/shared/components/ui/PasswordRequirement";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import { OtpInput } from "@/shared/components/ui/OtpInput";
import { StoryCarousel } from "@/pages/onboarding/components/StoryCarousel";
import { supabase } from "@/shared/lib/supabase";
import { validatePassword } from "@/shared/utils/passwordValidation";
import { stripPhone } from "@/shared/utils/formatters";
import { LEGAL_ROUTES, FAMILY_ROUTES, POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { acceptClientInvitation } from "@/shared/utils/invitationAcceptance";
import { sentryUtils } from "@/shared/utils/sentry";
import { EventType, trackEvent } from "@/shared/utils/core";
import {
  toErrorType,
  trackOnboardingCompleted,
  trackUserSignedUp,
} from "@/shared/utils/funnelAnalytics";
import { useStepAnalytics } from "@/shared/hooks/useStepAnalytics";
import { usePricingViewTracking } from "@/shared/hooks/usePricingViewTracking";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import { CLIENT_INVITE_STORIES } from "./clientInviteStories";
import { ClientInviteError } from "./components/ClientInviteError";
import { ClientInvitePlanStep } from "./components/ClientInvitePlanStep";
import { ClientInviteAddonsStep } from "./components/ClientInviteAddonsStep";
import { ClientInviteWelcome } from "./components/ClientInviteWelcome";

import type { ClientInvitationData } from "./clientInviteTypes";
import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";
import type { PaymentTier } from "@/shared/utils/tierOrder";
import { SecurityNote } from "@/shared/components/ui/security-note";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

const PLAN_TO_TIER: Record<LawyerPlanKey, PaymentTier> = {
  estate_setup: "basic",
  asset_discovery: "premium",
  estate_administration: "platinum",
};

const OTP_LENGTH = 6;

type SignupStep = "form" | "verify" | "plan" | "addons" | "welcome";

const STEP_ANALYTICS: Record<SignupStep, { id: string; number: number }> = {
  form: { id: "client-invite-signup", number: 2 },
  verify: { id: "client-invite-verify", number: 3 },
  plan: { id: "client-invite-plan", number: 4 },
  addons: { id: "client-invite-addons", number: 5 },
  welcome: { id: "client-invite-welcome", number: 6 },
};

const ANALYTICS_FLOW = "attorney_invite";

export const ClientInviteSignupPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get("token") ?? "";
  const hasSessionId = searchParams.has("session_id");
  const stepParam = searchParams.get("step");
  const initialStep =
    stepParam === "welcome" && hasSessionId
      ? ("welcome" as const)
      : stepParam === "plan"
        ? ("plan" as const)
        : stepParam === "addons"
          ? ("addons" as const)
          : null;
  const isExistingUser = searchParams.get("existing") === "true";
  const verifyingRef = useRef(false);
  const passwordRef = useRef("");

  const {
    data: invitation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["client-invite-signup", token],
    queryFn: async (): Promise<ClientInvitationData> => {
      if (!token) throw new Error("invalid");

      const { data: result, error: fetchError } = await supabase.functions.invoke(
        "account/lookup-client-invite",
        { body: { token } },
      );

      if (fetchError || result?.error) throw new Error("invalid");

      const inv = result as ClientInvitationData;
      if (inv.status !== "pending" && inv.status !== "accepted") throw new Error("invalid");

      const expiresAt = new Date(inv.expires_at);
      if (expiresAt < new Date()) throw new Error("expired");

      return inv;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const [step, setStep] = useState<SignupStep>(initialStep ?? "form");

  const { trackFinalStepCompleted } = useStepAnalytics({
    stepId: invitation ? STEP_ANALYTICS[step].id : null,
    stepNumber: STEP_ANALYTICS[step].number,
    events: {
      viewed: EventType.ONBOARDING_STEP_VIEWED,
      completed: EventType.ONBOARDING_STEP_COMPLETED,
      back: EventType.ONBOARDING_STEP_BACK_CLICKED,
    },
    properties: { flow: ANALYTICS_FLOW },
    isResume: initialStep !== null,
  });

  const checkoutReturnTrackedRef = useRef(false);
  useEffect(() => {
    if (initialStep === "welcome" && !checkoutReturnTrackedRef.current) {
      checkoutReturnTrackedRef.current = true;
      trackEvent(EventType.CHECKOUT_RETURNED, {
        flow: ANALYTICS_FLOW,
        checkout_context: "client_invite",
        checkout_result: "success",
      });
    }
  }, [initialStep]);

  const goToStep = (next: "plan" | "addons" | "welcome") => {
    setStep(next);
    const url = new URL(window.location.href);
    url.searchParams.set("step", next);
    window.history.pushState(null, "", url.toString());
  };

  // Handle browser back/forward within the step flow
  useEffect(() => {
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const s = params.get("step");
      if (s === "plan" || s === "addons" || s === "welcome") {
        setStep(s);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  if (invitation && !initialized) {
    setFirstName(invitation.client_first_name ?? "");
    setLastName(invitation.client_last_name ?? "");
    setEmail(invitation.client_email ?? "");
    setPhone(invitation.client_phone ?? "");
    setInitialized(true);

    if (!initialStep && invitation.status === "accepted") {
      const next = invitation.recommended_plan ? "plan" : "addons";
      setStep(next);
      const url = new URL(window.location.href);
      url.searchParams.set("step", next);
      window.history.replaceState(null, "", url.toString());
    }
  }

  // If user already has a session and the invitation is accepted, skip the form
  useEffect(() => {
    if (step !== "form" || !invitation || invitation.status !== "accepted") return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        const next = invitation.recommended_plan ? "plan" : "addons";
        setStep(next);
        const url = new URL(window.location.href);
        url.searchParams.set("step", next);
        window.history.replaceState(null, "", url.toString());
      }
    });
  }, [step, invitation]);

  useEffect(() => {
    if (!isExistingUser || !invitation || invitation.status !== "pending") return;

    const acceptForExistingUser = async () => {
      setLoading(true);
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        if (!authUser) {
          navigate(
            `/login?redirect=${encodeURIComponent(`/client-invite/signup?token=${token}&existing=true`)}`,
          );
          return;
        }

        const accepted = await acceptClientInvitation(token, invitation.client_email);
        if (!accepted) {
          setAuthError("Failed to accept invitation. Please try again.");
          return;
        }

        await queryClient.invalidateQueries({ queryKey: ["app-user-data"], exact: false });
        await queryClient.invalidateQueries({ queryKey: ["client-invite-signup", token] });
        goToStep(invitation.recommended_plan ? "plan" : "addons");
      } catch {
        setAuthError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    acceptForExistingUser();
  }, [isExistingUser, invitation]);

  const passwordValidation = validatePassword(password);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!passwordValidation.allMet) errors.password = "Password does not meet requirements";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      trackEvent(EventType.ONBOARDING_VALIDATION_FAILED, {
        flow: ANALYTICS_FLOW,
        step_id: STEP_ANALYTICS.form.id,
        field_ids: Object.keys(errors).join(","),
      });
    }
    return Object.keys(errors).length === 0;
  };

  const handleCreateAccount = async () => {
    setAuthError(null);
    if (!validate()) return;

    setLoading(true);
    trackEvent(EventType.SIGNUP_SUBMITTED, { flow: ANALYTICS_FLOW, signup_method: "email" });
    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { data, error: invokeError } = await supabase.functions.invoke(
        "communications/verification-code/send",
        { body: { email: normalizedEmail, password } },
      );

      if (invokeError || data?.error) {
        trackEvent(EventType.SIGNUP_FAILED, {
          flow: ANALYTICS_FLOW,
          signup_method: "email",
          error_type: toErrorType(invokeError ?? data?.error),
        });
      }

      if (invokeError) {
        setAuthError("Failed to send verification code. Please try again.");
        return;
      }

      if (data?.error) {
        setAuthError(data.error as string);
        return;
      }

      trackEvent(EventType.VERIFICATION_CODE_SENT, { flow: ANALYTICS_FLOW, is_resend: false });

      sessionStorage.setItem("client_invite_email", normalizedEmail);
      passwordRef.current = password;
      sessionStorage.setItem("client_invite_token", token);
      sessionStorage.setItem("client_invite_name", `${firstName.trim()} ${lastName.trim()}`);
      sessionStorage.setItem("client_invite_phone", stripPhone(phone));

      setStep("verify");
    } catch (err) {
      trackEvent(EventType.SIGNUP_FAILED, {
        flow: ANALYTICS_FLOW,
        signup_method: "email",
        error_type: toErrorType(err),
      });
      const message = err instanceof Error ? err.message : "An unexpected error occurred";
      setAuthError(message);
      sentryUtils.captureError(err, "Client invite signup: send verification code failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = useCallback(async () => {
    if (verifyingRef.current || otp.length !== OTP_LENGTH) return;
    verifyingRef.current = true;
    setLoading(true);
    setAuthError(null);

    const storedEmail = sessionStorage.getItem("client_invite_email") ?? email.toLowerCase().trim();

    try {
      const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
        email: storedEmail,
        token: otp,
        type: "signup",
      });

      if (verifyError || !verifyData.user) {
        const errorType = toErrorType(verifyError);
        trackEvent(EventType.VERIFICATION_CODE_FAILED, {
          flow: ANALYTICS_FLOW,
          error_type: errorType === "unknown" ? "invalid_code" : errorType,
        });
        setAuthError("Invalid code. Please try again.");
        return;
      }

      const fullName =
        sessionStorage.getItem("client_invite_name") ?? `${firstName.trim()} ${lastName.trim()}`;
      const phoneNumber = sessionStorage.getItem("client_invite_phone") ?? stripPhone(phone);
      const nameParts = fullName.trim().split(/\s+/);

      const inviteToken = sessionStorage.getItem("client_invite_token") ?? token;
      const accepted = await acceptClientInvitation(inviteToken, storedEmail, {
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        phoneNumber,
      });

      if (!accepted) {
        trackEvent(EventType.SIGNUP_FAILED, {
          flow: ANALYTICS_FLOW,
          signup_method: "email",
          error_type: "unknown",
        });
        setAuthError("Failed to accept invitation. Please try again.");
        return;
      }

      trackUserSignedUp(ANALYTICS_FLOW, "email");

      sessionStorage.removeItem("client_invite_email");
      passwordRef.current = "";
      sessionStorage.removeItem("client_invite_token");
      sessionStorage.removeItem("client_invite_name");
      sessionStorage.removeItem("client_invite_phone");

      await queryClient.invalidateQueries({ queryKey: ["app-user-data"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["client-invite-signup", token] });

      goToStep(invitation?.recommended_plan ? "plan" : "addons");
    } catch {
      setAuthError("An unexpected error occurred");
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  }, [otp, email, firstName, lastName, phone, token, navigate, queryClient, invitation]);

  const handleResend = async () => {
    const storedEmail = sessionStorage.getItem("client_invite_email");
    if (!storedEmail || !passwordRef.current) return;

    setAuthError(null);
    setLoading(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "communications/verification-code/send",
        { body: { email: storedEmail, password: passwordRef.current } },
      );

      if (invokeError || data?.error) {
        setAuthError("Failed to resend code. Please try again.");
      } else {
        trackEvent(EventType.VERIFICATION_CODE_SENT, { flow: ANALYTICS_FLOW, is_resend: true });
      }
    } catch {
      setAuthError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === OTP_LENGTH && !loading && !verifyingRef.current) {
      handleVerify();
    }
  }, [otp, loading, handleVerify]);

  usePricingViewTracking({
    isOpen: step === "plan" && !!invitation?.recommended_plan,
    promptLocation: "client_invite_plan",
  });
  usePricingViewTracking({
    isOpen: step === "addons" && !!invitation,
    promptLocation: "client_invite_addons",
  });

  if (!token) return <ClientInviteError type="invalid" />;
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }
  if (error || !invitation) return <ClientInviteError type="invalid" />;

  const handleCheckout = async (tier: PaymentTier, addons: string[] = []) => {
    setLoading(true);
    const trackCheckoutFailed = (err: unknown) =>
      trackEvent(EventType.CHECKOUT_FAILED, {
        flow: ANALYTICS_FLOW,
        checkout_context: "client_invite",
        plan_type: tier,
        error_type: toErrorType(err),
      });
    try {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (!authUser) {
        setAuthError("Something went wrong. Please try again.");
        return;
      }

      const { data: userData } = await supabase
        .from("users")
        .select("user_id")
        .eq("auth_id", authUser.id)
        .single();
      if (!userData) {
        setAuthError("Something went wrong. Please try again.");
        return;
      }

      const { data: userEstate } = await supabase
        .from("user_estates")
        .select("estate_id")
        .eq("user_id", userData.user_id)
        .limit(1)
        .maybeSingle();

      const returnPath = `${FAMILY_ROUTES.CLIENT_INVITE_SIGNUP}?token=${token}&step=welcome`;
      const cancelStep = invitation.recommended_plan ? "plan" : "addons";
      const cancelPath = `${FAMILY_ROUTES.CLIENT_INVITE_SIGNUP}?token=${token}&step=${cancelStep}`;

      const { data, error: checkoutError } = await supabase.functions.invoke("billing/checkout", {
        body: {
          returnPath,
          cancelPath,
          userId: userData.user_id,
          estateId: userEstate?.estate_id,
          context: "client_invite",
          promptLocation: step === "addons" ? "client_invite_addons" : "client_invite_plan",
          sourcePath: window.location.pathname,
          createCustomer: true,
          mode: "payment",
          fromTier: "free",
          toTier: tier,
          invitationToken: token,
        },
      });

      if (checkoutError || !data?.url) {
        trackCheckoutFailed(checkoutError);
        setAuthError("Something went wrong. Please try again.");
        return;
      }

      trackEvent(EventType.CHECKOUT_STARTED, {
        flow: ANALYTICS_FLOW,
        checkout_context: "client_invite",
        plan_type: tier,
        has_promotion_code: false,
        addons: addons.join(","),
      });
      window.location.href = data.url;
    } catch (err) {
      trackCheckoutFailed(err);
      setAuthError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const recommendedTier = invitation.recommended_plan
    ? PLAN_TO_TIER[invitation.recommended_plan as LawyerPlanKey]
    : undefined;

  const trackPlanSelected = (
    tier: PaymentTier,
    addons: string[],
    selectionSource: "recommendation" | "all_plans",
  ) =>
    trackEvent(EventType.CLIENT_INVITE_PLAN_SELECTED, {
      flow: ANALYTICS_FLOW,
      plan_type: tier,
      recommended_plan_type: recommendedTier ?? "none",
      is_recommended_plan: tier === recommendedTier,
      addons: addons.join(","),
      selection_source: selectionSource,
    });

  if (step === "welcome") {
    return (
      <ClientInviteWelcome
        planKey={invitation.recommended_plan as LawyerPlanKey | null}
        lawyerName={invitation.lawyer_name}
        onGetStarted={() => {
          trackFinalStepCompleted();
          trackOnboardingCompleted(ANALYTICS_FLOW, { has_paid: hasSessionId });
          navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
        }}
      />
    );
  }

  if (step === "plan" && invitation.recommended_plan) {
    const planTier = PLAN_TO_TIER[invitation.recommended_plan as LawyerPlanKey];
    return (
      <ClientInvitePlanStep
        planKey={invitation.recommended_plan as LawyerPlanKey}
        priceCents={invitation.plan_price_cents}
        lawyerName={invitation.lawyer_name}
        onContinue={() => {
          trackPlanSelected(planTier, [], "recommendation");
          handleCheckout(planTier);
        }}
        onSeeAddons={() => goToStep("addons")}
      />
    );
  }

  if (step === "addons") {
    return (
      <ClientInviteAddonsStep
        recommendedPlan={(invitation.recommended_plan as LawyerPlanKey) ?? null}
        lawyerName={invitation.lawyer_name}
        firstClientOffer={invitation.first_client_offer_applied}
        onContinue={(addons) => {
          const highestTier: PaymentTier = addons.includes("estate_administration")
            ? "platinum"
            : addons.includes("asset_discovery")
              ? "premium"
              : "basic";
          trackPlanSelected(highestTier, addons, "all_plans");
          handleCheckout(highestTier, addons);
        }}
      />
    );
  }

  return (
    <div className="flex h-screen bg-subtle">
      <div className="relative flex flex-1 flex-col items-center justify-center overflow-y-auto p-4 sm:p-10">
        <img src={logoFull} alt="Elayne" className="absolute left-4 top-4 h-8 sm:left-8 sm:top-8" />

        {step === "form" ? (
          <div className="flex w-full max-w-[500px] flex-col items-center gap-6">
            <Tag color="white">
              Invited by {invitation.lawyer_name}
              {invitation.firm_name ? ` from ${invitation.firm_name}` : ""}
            </Tag>

            <div className="flex flex-col gap-3 text-center">
              <h2>Set up your account</h2>
              <p className="text-sm text-secondary">
                Confirm the details your attorney shared, then create your password
              </p>
            </div>

            <div className="flex w-full flex-col gap-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="invite-first-name">First name</Label>
                  <Input
                    id="invite-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={fieldErrors.firstName ? "border-destructive" : undefined}
                  />
                  {fieldErrors.firstName && <FieldError message={fieldErrors.firstName} />}
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="invite-last-name">Last name</Label>
                  <Input
                    id="invite-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={fieldErrors.lastName ? "border-destructive" : undefined}
                  />
                  {fieldErrors.lastName && <FieldError message={fieldErrors.lastName} />}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-email">Email address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  readOnly
                  disabled
                  className="bg-subtle"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-phone">
                  Phone number
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipInfoTrigger
                        aria-label={WHY_WE_ASK_TITLE}
                        className="ml-1 align-middle"
                      />
                      <TooltipContent title={WHY_WE_ASK_TITLE}>
                        {WHY_WE_ASK.userPhone}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <PhoneNumberInput id="invite-phone" value={phone} onChange={setPhone} />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-password">Password</Label>
                <div className="relative">
                  <Input
                    id="invite-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={fieldErrors.password ? "border-destructive pr-10" : "pr-10"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="flex flex-col gap-1 pt-1">
                    {passwordValidation.allMet ? (
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" strokeWidth={3} />
                        <span className="text-sm text-primary">All requirements met</span>
                      </div>
                    ) : (
                      <>
                        <PasswordRequirement
                          met={passwordValidation.hasMinLength}
                          text="At least 8 characters"
                        />
                        <PasswordRequirement
                          met={passwordValidation.hasMixedCase}
                          text="Upper and lowercase letters"
                        />
                        <PasswordRequirement
                          met={passwordValidation.hasNumber}
                          text="At least one number"
                        />
                      </>
                    )}
                  </div>
                )}
                {fieldErrors.password && <FieldError message={fieldErrors.password} />}
              </div>

              {authError && <FieldError message={authError} />}

              <Button
                variant="brand"
                rounded
                onClick={handleCreateAccount}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Create account
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>

              <p className="text-center text-2xs text-secondary">
                By signing up, you agree to our{" "}
                <Link to={LEGAL_ROUTES.TERMS_OF_SERVICE} className="text-brand underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to={LEGAL_ROUTES.PRIVACY_POLICY} className="text-brand underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        ) : (
          <div className="flex w-full max-w-[500px] flex-col items-center gap-10">
            <div className="flex flex-col items-center gap-3">
              <h2>Check your inbox</h2>
              <p className="text-sm text-secondary text-center">
                We sent a 6-digit code to <span className="font-medium text-primary">{email}</span>
              </p>
            </div>

            <div className="flex flex-col items-center gap-6">
              <OtpInput value={otp} onChange={setOtp} disabled={loading} error={!!authError} />
              <p className="text-sm text-secondary">
                Didn't get it? Check your spam folder or{" "}
                <Button
                  variant="link"
                  size={null}
                  type="button"
                  onClick={handleResend}
                  className="inline text-sm text-brand underline"
                >
                  resend code
                </Button>
              </p>
            </div>

            {authError && <FieldError message={authError} />}

            <div className="flex w-full flex-col items-center gap-6">
              <Button
                variant="brand"
                rounded
                onClick={handleVerify}
                disabled={loading || otp.length !== OTP_LENGTH}
                className="w-full"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>

              <SecurityNote
                align="left"
                text="Bank-level encryption, and we never sell your information"
              />
            </div>
          </div>
        )}
      </div>

      <div className="hidden p-4 pl-0 lg:flex lg:w-[500px]">
        <StoryCarousel stories={CLIENT_INVITE_STORIES} className="flex-1" />
      </div>
    </div>
  );
};
